import React, { useState } from "react";
import { Box, Typography, Paper, Tabs, Tab, Container } from "@mui/material";
import { useUser, UserData } from "../UserContext";
import AvailabilityManager from "./AvailabilityManager";
import Payments from "./Payments";
import Autopick from "./Autopick";
import Notifications from "./Notifications";
import UserInfo from "./UserInfo";
import Brands from "./Admin/Brand/Brands";
import Products from "./Admin/Brand/Products";
import Team from "./Admin/Brand/BrandStaff";
import AccountStaff from "./Admin/Account/AccountStaff";
import Accounts from "./Admin/Account/Accounts";
import BASettings from "./Admin/BA/BASettings";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      style={{ display: value === index ? "block" : "none" }}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      <Box sx={{ p: 3 }}>{children}</Box>
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    "aria-controls": `settings-tabpanel-${index}`,
  };
}

const Settings: React.FC = () => {
  const { currentUser } = useUser();
  const [value, setValue] = useState(0);
  const { selectedAccountId, selectedAmbassadorId } = useUser() as {
    currentUser: UserData;
    selectedAccountId: number | null;
    selectedBrandId: number | null;
    selectedAmbassadorId: number | null;
    selectedAgencyId?: number | null;
  };

  // Handle tab changes within Settings
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const renderContent = () => {
    switch (currentUser?.role) {
      case "Account":
      case "Owner":
        const isAccountAdmin =
          currentUser?.user_access?.user_status?.Admin === "Yes";
        const isOwnerAdmin =
          currentUser?.role === "Owner" &&
          currentUser?.user_access?.user_status?.Admin === "Yes";

        return (
          <>
            <Tabs value={value} onChange={handleChange}>
              <Tab label="Info" {...a11yProps(0)} />
              <Tab label="Availability" {...a11yProps(1)} />
              <Tab label="Notifications" {...a11yProps(2)} />
              {isAccountAdmin && <Tab label="Team" {...a11yProps(4)} />}
              {isOwnerAdmin && <Tab label="Accounts" {...a11yProps(5)} />}
            </Tabs>

            <TabPanel value={value} index={0}>
              <UserInfo />
            </TabPanel>

            <TabPanel value={value} index={1}>
              <AvailabilityManager accountId={selectedAccountId} />
            </TabPanel>

            <TabPanel value={value} index={2}>
              <Typography variant="h6" gutterBottom>
                Notification Settings
              </Typography>
              <Notifications />
            </TabPanel>

            {isAccountAdmin && (
              <TabPanel value={value} index={4}>
                <AccountStaff />
              </TabPanel>
            )}

            {isOwnerAdmin && (
              <TabPanel value={value} index={5}>
                <Accounts />
              </TabPanel>
            )}
          </>
        );

      case "BA":
        return (
          <>
            <Tabs value={value} onChange={handleChange}>
              <Tab label="User" {...a11yProps(0)} />
              <Tab label="Account" {...a11yProps(1)} />
              <Tab label="Availability" {...a11yProps(2)} />
              <Tab label="Notifications" {...a11yProps(4)} />
            </Tabs>
            <TabPanel value={value} index={0}>
              <UserInfo />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <BASettings />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <AvailabilityManager baId={selectedAmbassadorId} />
            </TabPanel>
            <TabPanel value={value} index={4}>
              <Typography variant="h6" gutterBottom>
                Notification Settings
              </Typography>
              <Notifications />
            </TabPanel>
          </>
        );

      case "Brand":
      case "Supplier":
        const isAdminSupplier =
          currentUser?.user_access?.user_status?.Admin === "Yes" &&
          currentUser.role === "Supplier";

        // Define all possible tabs
        const tabs = [
          { label: "Info", component: <UserInfo /> },
          ...(isAdminSupplier
            ? [
                { label: "Team", component: <Team /> },
                { label: "Brands", component: <Brands /> },
                { label: "Products", component: <Products /> },
              ]
            : currentUser?.user_access?.user_status?.Admin === "Yes" &&
              currentUser.role === "Brand"
            ? [
                { label: "Team", component: <Team /> },
                { label: "Products", component: <Products /> },
              ]
            : []),
          {
            label: "Notifications",
            component: (
              <>
                <Typography variant="h6" gutterBottom>
                  Notification Settings
                </Typography>
                <Notifications />
              </>
            ),
          },
          {
            label: "Payment Method",
            component: (
              <>
                <Typography variant="h6" gutterBottom>
                  Payment Method
                </Typography>
                <Payments />
              </>
            ),
          },
          {
            label: "Autopick",
            component: (
              <>
                <Typography variant="h6" gutterBottom>
                  Autopick Settings
                </Typography>
                <Autopick />
              </>
            ),
          },
        ];

        return (
          <>
            <Tabs value={value} onChange={handleChange}>
              {tabs.map((tab, index) => (
                <Tab key={tab.label} label={tab.label} {...a11yProps(index)} />
              ))}
            </Tabs>

            {tabs.map((tab, index) => (
              <TabPanel key={tab.label} value={value} index={index}>
                {tab.component}
              </TabPanel>
            ))}
          </>
        );
      case "Agency":
        return (
          <>
            <Tabs value={value} onChange={handleChange}>
              <Tab label="Info" {...a11yProps(0)} />
              <Tab label="Notifications" {...a11yProps(2)} />
            </Tabs>

            <TabPanel value={value} index={0}>
              <UserInfo />
            </TabPanel>

            <TabPanel value={value} index={2}>
              <Typography variant="h6" gutterBottom>
                Notification Settings
              </Typography>
              <Notifications />
            </TabPanel>
          </>
        );
      default:
        return <Typography>Please log in to view settings</Typography>;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 4 }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h4" sx={{ mr: 2 }}>
              Settings
            </Typography>
            {currentUser?.user_access?.user_status?.Admin === "Yes" && (
              <Box
                sx={{
                  border: "1px solid",
                  borderRadius: "12px",
                  padding: "4px 8px",
                  display: "inline-block",
                  borderColor: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.primary.main,
                }}
              >
                <Typography variant="body2" color="primary">
                  ADMIN
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {renderContent()}
      </Paper>
    </Container>
  );
};

export default Settings;
